import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, X } from "lucide-react";
import { api } from "../../lib/api";
import { Logo } from "./Logo";

const KEY = "wouffa_chat_session";
const WELCOME = "Bonjour. Écrivez-moi ici en toute discrétion. Rappel : SI PAS DE RÉSULTAT, VOUS NE PAYEZ PAS ! Vos informations restent 100% anonymes ici.";

const Bubble = ({ m }) => {
  const mine = m.sender === "visitor";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${mine
        ? "rounded-br-sm bg-amber-400 text-stone-950" : "rounded-bl-sm border border-amber-900/50 bg-[#181512] text-stone-100"}`}>
        {!mine && <p className="mb-0.5 font-mono-x text-[11px] uppercase tracking-wider text-amber-400">Maître Wouffa</p>}
        <p className="whitespace-pre-wrap break-words">{m.text}</p>
      </div>
    </div>
  );
};

const useChat = (open) => {
  const [sid, setSid] = useState(() => localStorage.getItem(KEY));
  const [msgs, setMsgs] = useState([]);
  const last = msgs.length ? msgs[msgs.length - 1].created_at : null;

  const merge = useCallback((list) => {
    if (!Array.isArray(list) || !list.length) return;
    setMsgs((prev) => {
      const ids = new Set(prev.map((m) => m.id));
      return [...prev, ...list.filter((m) => !ids.has(m.id))];
    });
  }, []);

  useEffect(() => {
    if (!sid) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/chat/${sid}/messages`, { params: last ? { after: last } : {} });
        if (alive) merge(data);
      } catch (e) {
        if (e?.response?.status === 404) { localStorage.removeItem(KEY); setSid(null); setMsgs([]); }
      }
    };
    tick();
    const t = setInterval(tick, open ? 2000 : 8000);
    return () => { alive = false; clearInterval(t); };
  }, [sid, last, open, merge]);

  const sendTo = useCallback(async (id, text) => {
    const { data } = await api.post(`/chat/${id}/messages`, { text });
    merge(data);
  }, [merge]);

  const send = useCallback(async (text) => {
    let id = sid;
    try {
      if (!id) {
        const { data } = await api.post("/chat/sessions", {});
        id = data?.session_id;
        localStorage.setItem(KEY, id);
        setSid(id);
      }
      await sendTo(id, text);
    } catch (e) {
      if (e?.response?.status === 404) {
        // session perdue (ex : changement de serveur) -> nouvelle session + renvoi automatique
        const { data } = await api.post("/chat/sessions", {});
        localStorage.setItem(KEY, data?.session_id);
        setSid(data?.session_id);
        setMsgs([]);
        await sendTo(data?.session_id, text);
      } else {
        throw e;
      }
    }
  }, [sid, sendTo]);
  return { msgs, send };
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const { msgs, send } = useChat(open);
  const scroller = useRef(null);
  const input = useRef(null);

  useEffect(() => {
    const h = (e) => { setOpen(true); if (e.detail?.prefill) setText(e.detail.prefill); setTimeout(() => input.current?.focus(), 350); };
    window.addEventListener("wouffa:open-chat", h);
    return () => window.removeEventListener("wouffa:open-chat", h);
  }, []);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [msgs, open]);

  const submit = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true); setError("");
    try { await send(t); setText(""); } catch { setError("Envoi impossible. Réessayez."); }
    setSending(false);
  };

  return (
    <div className="fixed bottom-4 right-4 z-[70] flex flex-col items-end gap-3 md:bottom-6 md:right-6">
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="flex h-[min(560px,calc(100svh-110px))] w-[calc(100vw-2rem)] max-w-[380px] flex-col overflow-hidden rounded-3xl border border-amber-600/50 bg-[#0d0b0a]/95 shadow-[0_30px_80px_rgba(0,0,0,0.7)] backdrop-blur-xl"
            data-testid="chat-panel">
            <div className="flex items-center justify-between border-b border-amber-900/50 bg-[#12100e] px-5 py-4">
              <div className="flex items-center gap-3">
                <Logo size={34} />
                <div>
                  <p className="font-display text-sm text-amber-100">Maître Wouffa</p>
                  <p className="flex items-center gap-1.5 text-xs text-emerald-400"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Chat instantané · confidentiel</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Fermer" data-testid="chat-close-button"
                className="rounded-full p-2 text-stone-300 transition-colors hover:bg-amber-900/30 hover:text-amber-200"><X className="h-4 w-4" /></button>
            </div>
            <div ref={scroller} data-lenis-prevent className="flex-1 space-y-3 overflow-y-auto px-4 py-5" data-testid="chat-messages">
              <Bubble m={{ sender: "master", text: WELCOME }} />
              {msgs.map((m) => <div key={m.id} data-testid={`chat-msg-${m.sender}`}><Bubble m={m} /></div>)}
            </div>
            {error && <p className="px-4 pb-1 text-xs text-red-400" data-testid="chat-error">{error}</p>}
            <form onSubmit={submit} className="flex items-center gap-2 border-t border-amber-900/50 bg-[#12100e] p-3">
              <input ref={input} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000}
                placeholder="Écrivez votre message ici..." data-testid="chat-input"
                className="flex-1 rounded-full border border-amber-900/50 bg-[#0a0908] px-4 py-3 text-base text-stone-100 outline-none placeholder:text-stone-500 focus:border-amber-500/70 md:text-sm" />
              <button type="submit" disabled={sending || !text.trim()} data-testid="chat-send-button" aria-label="Envoyer"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-400 text-stone-950 transition-transform duration-200 hover:scale-105 disabled:opacity-40">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button onClick={() => setOpen((o) => !o)} whileTap={{ scale: 0.92 }} data-testid="chat-toggle-button"
        className="relative flex items-center gap-3 rounded-full border border-amber-400/60 bg-[#12100e] py-2 pl-2 pr-5 shadow-[0_10px_40px_rgba(212,175,55,0.25)]">
        <span className="relative flex h-10 w-10 items-center justify-center rounded-full bg-amber-400 text-stone-950">
          {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
          {!open && <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-[#12100e] bg-emerald-400" />}
        </span>
        <span className="text-sm font-semibold text-amber-100">{open ? "Fermer" : "Chat instantané"}</span>
      </motion.button>
    </div>
  );
};
